import closelight from "@assets/navigation/close-light.svg";
import SlideTransition from "@components/transitions/SlideTransitions";
import React, { ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppState } from "src/redux/reducer";

interface SlidingPanelWebProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  heading?: string;
  headingColor?: string;
  showHeader?: boolean;
  showCurrentAccount?: boolean;
  hasBackButton?: boolean;
  endComponent?: React.ReactNode;
  widthDesktop?: string;
}

const SlidingPanelWeb: React.FC<SlidingPanelWebProps> = ({
  isOpen,
  onClose,
  children,
  heading = "",
  headingColor = "text-text-base",
  showHeader = true,
  showCurrentAccount = false,
  hasBackButton = false,
  endComponent,
  widthDesktop = "35%",
}) => {
  const navigate = useNavigate();
  const currentMerchantId = useSelector(
    (state: { user: { currentMerchantId: number } }) =>
      state.user.currentMerchantId,
  );
  const userDetails = useSelector((state: AppState) => state.user);
  const merchantName =
    userDetails.merchantDetails.find(
      (merchant) => merchant.merchantId === currentMerchantId,
    )?.categoryName ??
    userDetails.merchantDetails.find(
      (merchant) => merchant.merchantId === currentMerchantId,
    )?.merchantName;
  const userLabel =
    currentMerchantId === -1
      ? userDetails.accountType === "institutional" ||
        userDetails.accountType === "settlement"
        ? "Checking Account Wallet"
        : userDetails.accountType === "affiliate"
          ? "Affiliate Wallet"
          : "Personal Wallet"
      : merchantName;

  useEffect(() => {
    const handleResize = () => {
      console.log("Window resized, keyboard might be opening or closing");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const panelStyle: React.CSSProperties = {
    width: widthDesktop,
    transition: "transform 0.3s ease-in-out",
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-end transition-opacity ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        zIndex: 1000,
        backgroundColor: isOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}>
      <div
        className="h-full bg-bg-page-body transform transition-transform flex flex-col" // Added flex and flex-col
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}>
        {showHeader ? (
          <SlideTransition className="fixed h-[58px] top-0 w-full z-50 flex flex-row justify-between px-4 py-6 bg-bg-base items-center">
            <div className="flex flex-row items-center">
              {hasBackButton ? (
                <span
                  className="material-symbols-outlined mr-2 cursor-pointer"
                  onClick={() => {
                    navigate(-1);
                  }}>
                  arrow_back_ios
                </span>
              ) : null}
              <span
                className={`${headingColor} text-[16px] font-normal mr-[10px]`}>
                {heading}
              </span>
              {showCurrentAccount ? (
                <div className="flex items-center bg-bg-header rounded-lg  px-[8px] py-[4px]">
                  <span className="font-medium text-text-base text-[10px]">
                    {userLabel}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="flex flex-row items-center gap-[4px]">
              {endComponent && <div>{endComponent}</div>}
              <img
                src={closelight}
                alt="Close"
                className="ml-2 cursor-pointer"
                onClick={onClose}
              />
            </div>
          </SlideTransition>
        ) : (
          <div className="px-4" />
        )}

        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

export default SlidingPanelWeb;
